import { memo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Swords, Bot, Lightbulb } from "lucide-react";
import { getRecentAISummary, getStrategyHint } from "@/game/gameHelpers";

const STRATEGY_TONE = {
  green: "bg-[#00E676]/15 border-[#00E676]/40 text-[#00E676]",
  red: "bg-[#FF5A5F]/15 border-[#FF5A5F]/40 text-[#FF5A5F]",
  amber: "bg-[#FFC107]/15 border-[#FFC107]/40 text-[#FFC107]",
  blue: "bg-[#38B6FF]/15 border-[#38B6FF]/40 text-[#38B6FF]",
};

const VERB_TONE = {
  buy: "text-[#00E676]",
  sell: "text-[#FF5A5F]",
  hold: "text-[#FFC107]",
  buy_fail: "text-[#FF5A5F]",
};

/**
 * Blocking "Round N / Player's Turn / I'm Ready" overlay.
 *
 * Shown whenever the current player index changes (for humans only in
 * vs-AI / Daily modes — bots skip this). Renders a strategy hint and,
 * if bots played since the user's last turn, a "While you waited" recap.
 *
 * Clicking "I'm Ready" triggers `onReady()` which the parent wires to
 * dispatch the `confirm_turn` action. Nothing below this overlay is
 * reachable while it's on screen — the parent early-returns when shown.
 */
function TurnTransitionOverlay({ game, currentPlayer, onReady, loading = false }) {
  const { t } = useTranslation();
  const aiSummary = getRecentAISummary(game, currentPlayer?.name);
  const strategy = getStrategyHint(game, game.current_player_index);

  return (
    <div className="turn-overlay" data-testid="turn-transition-overlay">
      <div className="text-center text-white max-w-md px-4">
        <Swords className="w-16 h-16 mx-auto mb-4 opacity-80" />
        <p className="text-lg opacity-70 mb-2">{t("turnTransition.round", { round: game.round_number })}</p>
        <h2
          className="text-4xl sm:text-5xl font-bold mb-5"
          style={{ fontFamily: "Fredoka" }}
        >
          {t("turnTransition.turnOf", { name: currentPlayer.name })}
        </h2>

        {aiSummary.length > 0 && (
          <div
            className="mb-3 rounded-2xl bg-white/10 dark:bg-slate-900/10 backdrop-blur border border-white/15 p-3 text-left"
            data-testid="ai-recap-chip"
          >
            <p
              className="text-[10px] font-bold uppercase tracking-[0.18em] opacity-60 mb-1.5"
              style={{ fontFamily: "Fredoka" }}
            >
              {t("turnTransition.whileWaited")}
            </p>
            <ul className="space-y-1 mb-2">
              {aiSummary.map((s) => (
                <li key={s.name} className="flex items-center gap-2 text-sm">
                  <Bot className="w-3.5 h-3.5 opacity-70 shrink-0" />
                  <span className="font-bold">{s.name}</span>
                  <span className={VERB_TONE[s.type] || "opacity-90"}>{s.text}</span>
                </li>
              ))}
            </ul>
            <div className="pt-2 border-t border-white/15 flex flex-wrap gap-x-3 gap-y-1 text-xs">
              {game.players.map((p, i) => (
                <span key={i} data-testid={`recap-score-${i}`}>
                  <span className="font-bold opacity-80">
                    {p.name === currentPlayer.name ? t("turnTransition.you") : p.name}
                  </span>
                  <span className="ml-1 text-[#FFC107] font-bold tabular-nums">
                    ${p.crypto_value}
                  </span>
                </span>
              ))}
            </div>
          </div>
        )}

        {strategy && (
          <div
            className={`mb-5 rounded-2xl border px-3 py-2 flex items-start gap-2 text-left ${
              STRATEGY_TONE[strategy.tone] || STRATEGY_TONE.blue
            }`}
            data-testid="strategy-hint"
          >
            <Lightbulb className="w-4 h-4 mt-0.5 shrink-0" />
            <div className="min-w-0">
              <p
                className="text-[10px] font-bold uppercase tracking-[0.15em] opacity-80"
                style={{ fontFamily: "Fredoka" }}
              >
                {t("turnTransition.strategy")}
              </p>
              <p className="text-[13px] leading-snug text-white/95" data-testid="strategy-hint-text">
                {strategy.text}
              </p>
            </div>
          </div>
        )}

        <Button
          data-testid="ready-btn"
          className="rounded-full px-10 py-4 text-lg font-bold bg-[#38B6FF] hover:bg-[#2196F3] text-white shadow-[0_4px_0_rgba(0,0,0,0.3)] hover:shadow-[0_2px_0_rgba(0,0,0,0.3)] hover:translate-y-[2px] transition-all active:scale-95 disabled:opacity-70 disabled:cursor-wait"
          onClick={onReady}
          disabled={loading}
        >
          {loading ? t("turnTransition.button.starting") : t("turnTransition.button.ready")}
        </Button>
      </div>
    </div>
  );
}

export default memo(TurnTransitionOverlay);
